import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

const EnvironmentCheck = () => {
  const [envVars, setEnvVars] = useState({});
  const [supabaseStatus, setSupabaseStatus] = useState('checking');
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    checkEnvironment();
  }, []);

  const checkEnvironment = async () => {
    // Check environment variables
    const vars = {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || 'NOT SET',
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY
        ? import.meta.env.VITE_SUPABASE_ANON_KEY.substring(0, 20) + '...'
        : 'NOT SET',
      VITE_SUPABASE_STORAGE_BUCKET: import.meta.env.VITE_SUPABASE_STORAGE_BUCKET || 'NOT SET',
      VITE_API_URL: import.meta.env.VITE_API_URL || 'NOT SET',
      VITE_APP_ENV: import.meta.env.VITE_APP_ENV || 'NOT SET',
      VITE_DEBUG: import.meta.env.VITE_DEBUG || 'NOT SET',
      VITE_BYPASS_AUTH: import.meta.env.VITE_BYPASS_AUTH || 'NOT SET',
      VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION || 'NOT SET'
    };

    setEnvVars(vars);

    // Test Supabase connection
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey ||
          supabaseUrl === 'https://placeholder.supabase.co' ||
          supabaseKey === 'placeholder-anon-key') {
        setSupabaseStatus('not-configured');
        setConnectionError('Missing or invalid Supabase credentials');
        return;
      }

      // Try a simple API call to test the connection
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        }
      });

      if (response.status === 401) {
        setSupabaseStatus('invalid-key');
        setConnectionError('Invalid API key - check your Supabase anon key');
      } else if (response.status === 404) {
        setSupabaseStatus('invalid-url');
        setConnectionError('Invalid Supabase URL - check your project URL');
      } else if (response.ok || response.status === 400) {
        // 400 is expected when hitting the root endpoint
        setSupabaseStatus('connected');
        setConnectionError(null);
      } else {
        setSupabaseStatus('error');
        setConnectionError(`Unexpected status: ${response.status}`);
      }
    } catch (error) {
      setSupabaseStatus('error');
      setConnectionError(error.message);
    }
  };

  const getStatusIcon = (value) => {
    if (value === 'NOT SET') {
      return <XCircle className="h-5 w-5 text-red-500" />;
    }
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  };

  const getConnectionStatusIcon = () => {
    switch (supabaseStatus) {
      case 'checking':
        return <RefreshCw className="h-5 w-5 text-yellow-500 animate-spin" />;
      case 'connected':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'not-configured':
      case 'invalid-key':
      case 'invalid-url':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <AlertTriangle className="h-6 w-6 mr-2 text-yellow-500" />
          Environment Configuration Check
        </h2>

        <div className="space-y-6">
          {/* Environment Variables */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Environment Variables</h3>
            <div className="space-y-2">
              {Object.entries(envVars).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <span className="font-mono text-sm">{key}</span>
                  <div className="flex items-center space-x-2">
                    <span className={`font-mono text-sm ${value === 'NOT SET' ? 'text-red-500' : 'text-green-600'}`}>
                      {value}
                    </span>
                    {getStatusIcon(value)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Supabase Connection Status */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Supabase Connection</h3>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
              <div>
                <p className="font-medium">Connection Status</p>
                {connectionError && (
                  <p className="text-sm text-red-500 mt-1">{connectionError}</p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className={`font-medium ${
                  supabaseStatus === 'connected' ? 'text-green-600' :
                  supabaseStatus === 'checking' ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {supabaseStatus === 'connected' ? 'Connected' :
                   supabaseStatus === 'checking' ? 'Checking...' :
                   supabaseStatus === 'not-configured' ? 'Not Configured' :
                   supabaseStatus === 'invalid-key' ? 'Invalid API Key' :
                   supabaseStatus === 'invalid-url' ? 'Invalid URL' : 'Error'}
                </span>
                {getConnectionStatusIcon()}
              </div>
            </div>
          </div>

          {/* Instructions */}
          {supabaseStatus !== 'connected' && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-semibold text-yellow-800 mb-2">Action Required:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-yellow-700">
                <li>Go to your Vercel dashboard: https://vercel.com/dashboard</li>
                <li>Navigate to your project settings</li>
                <li>Add all missing environment variables (shown as "NOT SET" above)</li>
                <li>Redeploy your application</li>
                <li>Clear your browser cache and refresh this page</li>
              </ol>
            </div>
          )}

          <button
            onClick={checkEnvironment}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Re-check Configuration</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentCheck;